<script setup lang="ts">
import type { VueInstance } from '@vueuse/core';
import type { Chapter, LearningActivity } from '~/composables/useCourse';
import { CourseContentActivityType, CourseSubViewType } from "~/enums/course.enums";
import {
  KickoffMasteryTrackingEvents,
  KickoffMasteryTrackingParameters,
  KickoffMasteryTrackingValues,
} from "~/enums/tracking/kickoff-mastery.enums";

const props = defineProps<{
  activity: LearningActivity;
  isWorkshop: boolean;
  isKickoffAndMasteryQuizCourse?: boolean;
  chapter: Chapter;
}>();

const route = useRoute();
const { getPreviousActivity, getActivityUrl, subView } = useCourseState();
const { dispatchTrackingEvent } = useTracking();
const { t } = useI18n();
const localePath = useLocalePath();

const activityEl = ref<VueInstance | null>(null);

const isActive = computed(() => {
  if (props.activity.type === CourseContentActivityType.Comparison) {
    // todo: might not need the "baseName", just "name" should also work
    return route.meta.baseName === CourseContentActivityType.Comparison;
  }

  if (props.activity.type === CourseContentActivityType.Summary) {
    // todo: might not need the "baseName", just "name" should also work
    return route.meta.baseName === CourseContentActivityType.Summary;
  }

  const paramName = props.activity.type === CourseContentActivityType.Lesson ? 'lessonId' : 'quizId';
  return props.activity.id === parseInt(route.params[paramName] as string, 10);
});

const isCompleted = computed(() => {
  if (props.activity.type === CourseContentActivityType.Comparison) {
    return props.chapter.contents
      ?.find(content => content.type === CourseContentActivityType.MasteryQuiz)
      ?.progress?.finished;
  }

  return props.activity.completed || props.activity.progress?.finished;
});

const isLocked = computed(() => {
  if (props.activity.locked) {
    return true;
  }

  // workshop lessons are always accessible once the quiz (previous activity) is completed
  if (props.activity.type === CourseContentActivityType.Lesson && props.isWorkshop) {
    const previousActivity = getPreviousActivity(String(props.activity.id));
    if (previousActivity && !previousActivity.progress?.finished) {
      return true;
    }
  }

  return false;
});

const activityDuration = computed(() => {
  if (props.activity.type !== CourseContentActivityType.Lesson || !props.activity.duration) {
    return null;
  }

  const durationInSeconds = props.activity.duration;
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds - (hours * 3600)) / 60);
  const seconds = durationInSeconds - (hours * 3600) - (minutes * 60);

  return `${hours ? `${hours}:` : ''}${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
});

const scrollActiveIntoView = () => {
  if (isActive.value && activityEl.value) {
    nextTick(() => {
      setTimeout(()  => {
        activityEl.value?.$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    });
  }
};

onMounted(() => {
  scrollActiveIntoView();
});

watch(isActive, () => {
  scrollActiveIntoView();
});

const activityUrlForPlaylistItem = computed(() => {
  if (props.activity.type === CourseContentActivityType.MasteryQuiz && props.isKickoffAndMasteryQuizCourse && props.activity.progress?.finished) {
    return undefined;
  }

  const url = getActivityUrl(props.activity);

  if (!url) {
    return undefined;
  }

  return url;
});

const handleRedirect = () => {
  if (subView.value !== CourseSubViewType.Course) {
    return;
  }

  dispatchTrackingEvent(KickoffMasteryTrackingEvents.CourseStarted, {
    [KickoffMasteryTrackingParameters.CourseStartSource]: KickoffMasteryTrackingValues.LessonsList,
  });
};
</script>

<template>
  <NuxtLink
    ref="activityEl"
    v-tippy.bottom="
      activity.type === CourseContentActivityType.MasteryQuiz && isKickoffAndMasteryQuizCourse && activity.progress?.finished
        ? t('course.playlist.activity.tooltips.masteryAlreadyCompleted')
        : activity.locked && isKickoffAndMasteryQuizCourse
          ? activity?.type === CourseContentActivityType.MasteryQuiz ? t('course.playlist.activity.tooltips.activityLockedUntilKickoffAndChapters')
            : activity?.type === CourseContentActivityType.Comparison ? t('course.playlist.activity.tooltips.activityLockedUntilMasteryCompleted')
              : t('course.playlist.activity.tooltips.activityLockedUntilKickoffCompleted')
          : ''
    "
    class="flex px-4 py-2 gap-4 items-center justify-start text-decoration-none tracking-wide text-neutral-80"
    :class="[
      'course-playlist-activity',
      {'bg-blue-20 hover:!bg-blue-20 shadow-medmastery-light': isActive},
      {'hover:bg-blue-10': !!activityUrlForPlaylistItem},
      {'opacity-60': isLocked}
    ]"
    :to="localePath(activityUrlForPlaylistItem as string)"
    @click="handleRedirect()"
  >
    <ActivityStatusIcon
      class="shrink-0"
      :completed="isCompleted"
      :active="isActive"
      :activity-type="activity.type"
      :locked="isLocked"
      :passed="activity.progress?.passed"
    />
    <div
      class="text-16 leading-normal"
      :class="isActive ? 'text-neutral-100' : 'text-neutral-60'"
    >
      <span class="inline-block align-middle">
        <template v-if="activity.type === CourseContentActivityType.KickoffQuiz">
          {{t('course.playlist.activity.title.kickoffQuiz')}}
        </template>

        <template v-else-if="activity.type === CourseContentActivityType.MasteryQuiz">
          {{t('course.playlist.activity.title.masteryQuiz')}}
        </template>

        <template v-else-if="activity.type === CourseContentActivityType.Summary">
          {{t('course.playlist.activity.title.summary')}}
        </template>

        <template v-else>
          {{activity.title}}
        </template>
      </span>
    </div>

    <div
      class="info ml-auto text-14 leading-normal"
      :class="isActive ? 'text-neutral-100' : 'text-neutral-50'"
    >
      <span
        v-if="activity.type === CourseContentActivityType.Lesson"
        class="inline-block align-middle"
      >
        {{activityDuration}}
      </span>
      <span
        v-else-if="[CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(activity.type) && activity.progress?.finished"
        class="score inline-block align-middle"
        :class="{
          'text-red-50': activity.progress?.finished && !activity.progress?.passed
        }"
      >
        <template v-if="[CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(activity.type)">
          {{activity.progress.correctlyAnsweredRatio}}
        </template>

        <template v-else>
          {{(activity.progress?.score || 0)}}%
        </template>
      </span>
    </div>
  </NuxtLink>
</template>
